'use client'
import { useCallback, useMemo, useState, useEffect, memo } from 'react'
import type { ChangeEvent, JSX, ReactNode } from 'react'
import type { UseFormRegister as TypeRegister, FieldError, UseFormSetValue } from 'react-hook-form'
import { motion } from 'motion/react'
import styled, { css } from 'styled-components'
import { mq, Col, parsePlaceHolder, getTransition, vw, rgbAlpha, presets } from '@/styles'
import { MODALS } from '@/context'
import { useDropdown } from '../dropdown/useDropdown'
import { Img } from '../Img'
import { useModal } from '@/hooks'
import { TypeColors } from '@/schema'
import { getIcon } from '@/utils'

const noRegister = () => ({ onChange: null, onBlur: null, ref: null, name: null })

type onChangeType = (event: ChangeEvent<HTMLInputElement>) => void
export type TypeOption = {
  value: string
  name: string
  [key:string]:string
}
interface iSelect {
  options: TypeOption[]
  readonly name: string
  readonly label?: string
  placeholder?: string
  defaultValue?: string | { value: string, name: string }
  readonly required?: boolean
  readonly disabled?: boolean
  readonly autoComplete?: string
  readonly readOnly?: boolean
  readonly onChange?: onChangeType
  readonly col?: Col
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly register?: TypeRegister<any>
  error?: FieldError | { message: string, [key: string]: unknown }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFormValue?: UseFormSetValue<any>
  readonly icon?: string | boolean | ReactNode
  readonly className?: string
  readonly placeholderVisible?: boolean
  readonly textColor?: TypeColors
  readonly dropdownTextColor?: TypeColors
  readonly bgColor?: TypeColors
  modalId?: MODALS
  reset?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setReset?: any
  searchDisabled?: boolean
}

interface iLabelProps {
  $hasContent: boolean
  $placeholderVisible: boolean
  $textColor?: TypeColors
  $bgColor: TypeColors
}

interface iDivProps {
  $col: Col
  $readOnly?: boolean
}

const hasContentState = () => css`
  ${presets.font18InputLabel}
  opacity: 1;
  transform: translateY(-35px);
`

const optionsListVariants = {
  collapsed: {
    height: '0',
    opacity: 0,
    transitionEnd: {
      display: 'none',
    },
    transition: { ...getTransition() }
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    display: 'block',
    transition: { ...getTransition() }
  }
}

const getOptionsList = (data, onClickOption, currentValue) => data.map((option, idx) => {
  return (
    <li key={`${option.value}-${idx}`} className={`${currentValue === option.value ? 'current' : ''}${option.store ? ` online-store${data[idx+1]?.store ? '' : ' last-store'}` : ''}`}>
      <button type='button' onMouseDown={() => { onClickOption(option) }} data-dismiss={true} disabled={option.disabled}>
        {option.name}
      </button>
    </li>
  )
})



let TIMEOUT_ID = null
let TIMEOUT_ID_SEARCH = null

export const Select = memo(({ options, name = 'country_code', defaultValue, label, autoComplete = 'off', placeholder, placeholderVisible = false, required = false, disabled = false, readOnly = null, onChange = null, col, register = noRegister, icon = null, className, textColor = '--color-white', bgColor = '--color-white', error, setFormValue, dropdownTextColor = null, modalId, reset = false, setReset, searchDisabled = false }:iSelect):JSX.Element => {
  const requireProps = typeof required === 'boolean' ? { required } : required
  const { onChange:registerOnChange, onBlur:registerOnBlur, name:registerName, ref:registerRef } = register(name, { ...requireProps })
  const { isActive, payload } = useModal(modalId)
  const [triggerRef, dropdownRef, expanded, toggleExpanded, onTriggerFocus] = useDropdown()
  const [currentOptions, setCurrentOptions] = useState(options)
  const [isOptionClicked, setIsOptionClicked] = useState(false)

  const [fancyValue, setFancyValue] = useState(options.find(option => option.value === defaultValue)?.name ?? '')
  const [currentValue, setCurrentValue] = useState(defaultValue ?? '')


  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
  }

  const handleBlur = e => {
    registerOnBlur?.(e)
  }

  const onClickOption = (option) => {
    setIsOptionClicked(true)
    setFormValue?.(name, option.value)
    setCurrentValue(option.value)
    setFancyValue(option.value !== '' ? option.name : '')
    toggleExpanded()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Options = useMemo(() => getOptionsList(currentOptions, onClickOption, currentValue), [currentOptions, currentValue])

  const filterBySearch = useCallback(async ({ value, valid }) => {
    if (valid) {
      const data = options.filter(option => option.name.toLowerCase().includes(value.toLowerCase()))
      setCurrentOptions(data)
    } else {
      setCurrentOptions(options)
    }
  }, [options])

  const handleSearch = e => {
    if(searchDisabled) return
    const value = e?.target?.value
    setFancyValue(value)

    if(typeof value === 'string') {
      const valid = value.length > 1
      if(TIMEOUT_ID) clearTimeout(TIMEOUT_ID)
      TIMEOUT_ID = setTimeout(() => filterBySearch({value, valid}), 300)
    }
  }

  const onBlurSearch = useCallback(() => {
    if (isOptionClicked) {
      setIsOptionClicked(false)
      return
    }

    if(TIMEOUT_ID_SEARCH) clearTimeout(TIMEOUT_ID_SEARCH)
    TIMEOUT_ID_SEARCH = setTimeout(() => {
      setIsOptionClicked(false)
      const value = fancyValue
      let matchOption = null
      if (typeof value === 'string' && value.length > 2) {
        matchOption = options.filter(option => option.name.toLowerCase().includes(value.toLowerCase()))
        if (matchOption.length === 1) matchOption = matchOption[0]
      }

      setFormValue?.(name, matchOption?.value ?? '')
      setCurrentValue(matchOption?.value ??  '')
      setFancyValue(matchOption?.name ?? '')
      // Remove filtered options
      setCurrentOptions(options)
    }, 200)
  }, [fancyValue, isOptionClicked, name, options, setFormValue])

  useEffect(() => {
    setCurrentOptions(options)
  }, [options])

  useEffect(() => {
    let matchOption = null

    if (defaultValue) {
      matchOption = options.find(option => option.value === defaultValue)
      if (!matchOption) return
      setFormValue?.(name, matchOption.value)
      setCurrentValue(matchOption.value)
      setFancyValue(matchOption.name)
    }
  }, [defaultValue, options, name, setFormValue])

  useEffect(() => {
    // This useEffect will only run if the <Select /> is inside a modal
    if(!modalId && !isActive) return
    let matchOption = null

    matchOption = options.filter(option => option.name === payload?.product_name)?.[0]
    setFormValue?.(name, matchOption?.value ?? '')
    setCurrentValue(matchOption?.value ?? '')
    setFancyValue(matchOption?.name ?? '')
  }, [isActive, payload, options, name, setFormValue, modalId])


  useEffect(() => {
    if(!reset) return
    setFormValue?.(name, '')
    setCurrentValue('')
    setFancyValue('')
    setReset?.(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset])

  return (
    <Div ref={triggerRef} $col={col} $readOnly={readOnly}>
      <input type='hidden' name={registerName} required={required} aria-invalid={!!error}
        {...registerRef && { ref: registerRef }}
        {...defaultValue && { defaultValue: typeof defaultValue === 'string' ? defaultValue : defaultValue.name }}
        {...handleChange && { onChange: handleChange }}
        {...handleBlur && { onBlur: handleBlur }}
      />
      <Label $hasContent={!!fancyValue} $placeholderVisible={placeholderVisible || currentValue ? true : false} $textColor={textColor} $bgColor={bgColor} {...className && { className }}>
        <input type='text' name={`${name}_searcher`} disabled={disabled} readOnly={readOnly} placeholder={placeholder ?? label} onChange={handleSearch} onFocus={onTriggerFocus} onBlur={onBlurSearch} value={fancyValue} autoComplete={autoComplete} />
        {label && <span>{label}</span>}
        {icon ? icon : <Img media={textColor === '--color-white' ? getIcon('arrow-select--white') : getIcon('arrow-select')} fill={false} lazy={true} className='chevron'/>}
      </Label>
      {!readOnly && <Ul ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'} $textColor={textColor} $dropdownTextColor={dropdownTextColor}>
        {Options}
      </Ul>}
    </Div>
  )
})



const Div = styled.div<iDivProps>`
  padding: 0 4.25px;
  position: relative;
  pointer-events: ${({ $readOnly }) => $readOnly ? 'none' : 'auto'};
  width: 100%;

  ${ mq.greaterThan('tablet') } {
    padding: 0 10.5px;
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col };
  }

  ${mq.greaterThan('desktop-xs')} {
  }

  ${({ $readOnly }) => { if($readOnly) return css`
    .chevron {
      opacity: 0;
    }
  `}}
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Ul = styled(motion.ul)<any>`
  background: white;
  box-shadow: 4px 5px 6px 0px ${rgbAlpha('--color-black', .1)};
  margin-top: 1px;
  max-height: ${vw(28*5.5 + 4.25*6, 'mobile')};
  overflow-y: auto;
  padding: ${vw(8.5, 'mobile')} 0 ${vw(12.5, 'mobile')};
  position: absolute;
  top: ${vw(60, 'mobile')};
  width: calc(100% - ${vw(8.5, 'mobile')});
  z-index: 5;

  ${mq.greaterThan('nexus7')} {
    max-height: ${vw(20*5.5 + 4.25*6, 'tablet')};
    padding: ${vw(8.5, 'tablet')} 0 ${vw(12.5, 'tablet')};
    top: ${vw(60, 'tablet')};
    width: calc(100% - ${vw(8.5, 'tablet')});
  }

  ${mq.greaterThan('tablet')} {
    max-height: ${vw(32*5.5 + 4.25*6, 'desktop')};
    padding: ${vw(8.5, 'desktop')} 0 ${vw(12.5, 'desktop')};
    top: ${vw(60, 'desktop')};
    width: calc(100% - ${vw(25, 'desktop')});
  }

  ${mq.greaterThan('desktop')} {
    max-height: 208.5px;
    padding: 8.5px 0 12.5px;
    top: 60px;
    width: calc(100% - 25px);
  }

  li {
    margin-bottom: ${vw(4.25, 'mobile')};
    margin-left: ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(4.25, 'tablet')};
      margin-left: ${vw(10, 'tablet')};

    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(4.25, 'desktop')};
      margin-left: 0;

    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 4.25px;
    }

    &.online-store {
      &.last-store {
        margin-bottom: ${vw(8.25, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-bottom: ${vw(8.25, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-bottom: ${vw(8.25, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-bottom: 8.25px;
        }

        + li {
          border-top: 1px solid ${({ $textColor }) => rgbAlpha($textColor, .4)};
          margin-top: ${vw(8.25, 'mobile')};
          padding-top: ${vw(8.25, 'mobile')};

          ${mq.greaterThan('nexus7')} {
            margin-top: ${vw(8.25, 'tablet')};
            padding-top: ${vw(8.25, 'tablet')};
          }

          ${mq.greaterThan('tablet')} {
            margin-top: ${vw(8.25, 'desktop')};
            padding-top: ${vw(8.25, 'desktop')};
          }

          ${mq.greaterThan('desktop')} {
            margin-top: 8.25px;
            padding-top: 8.25px;
          }
        }
      }
    }

  }

  button {
    ${presets.font18_2}
    align-items: center;
    color: ${({ $textColor, $dropdownTextColor }) => $dropdownTextColor ? `var(${$dropdownTextColor})` : `var(${$textColor})`};
    display: flex;
    justify-content: space-between;
    opacity: .4;
    overflow-x: hidden;
    padding: ${vw(2, 'mobile')} ${vw(4.25, 'mobile')};
    text-overflow: ellipsis;
    transition: 300ms opacity ease-out;
    white-space: nowrap;
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(2, 'tablet')} ${vw(4.25, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(8, 'desktop')} ${vw(12.5, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 8px 12.5px;
    }

    &:hover {
      opacity: 1;
    }

    .online-store {
      opacity: .7;
    }
  }
`

const Label = styled.label<iLabelProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${vw(20, 'mobile')};
  position: relative;
  width: 100%;

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(20, 'tablet')};
  }

  ${ mq.greaterThan('tablet') } {
    margin-bottom: ${vw(30, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 30px;
  }

  > input {
    ${presets.font18_2}
    background-color: ${({ $bgColor }) => $bgColor ? `var(--color-${$bgColor})`: 'var(--color-white)'};
    border-bottom: 1px solid;
    border-color: ${({ $textColor }) => rgbAlpha($textColor, .4)};
    color: var(--color-text--darker);
    height: ${vw(60, 'mobile')};
    padding: ${vw(25, 'mobile')} 0 ${vw(12, 'mobile')};
    transition: 300ms border-color ease-out;
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      height: ${vw(60, 'tablet')};
      padding: ${vw(25, 'tablet')} 0 ${vw(12, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(60, 'desktop')};
      padding: ${vw(25, 'desktop')} 0 ${vw(12, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 60px;
      padding: 25px 0 12px;
    }

    ${({ $placeholderVisible, $textColor }) => parsePlaceHolder(`
      color: ${$placeholderVisible ? `var(${$textColor})` : 'rgba(0, 0, 0 ,0)'};
      opacity: ${$placeholderVisible ? .4 : 0};
    `)}

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: ${({  $textColor }) => `var(${$textColor})`};
      -webkit-box-shadow: 0 0 0 500px ${({ $bgColor }) => `var(${$bgColor})`} inset;
      box-shadow: 0 0 0 100px ${({ $bgColor }) => `var(${$bgColor})`} inset;
      color: inherit;
    }

    ${({ $placeholderVisible, $textColor }) => parsePlaceHolder(`
      opacity: ${$placeholderVisible ? .4 : 0};
      color: ${$placeholderVisible ? `var(${$textColor})` : 'rgba(0, 0, 0 ,0)'};
    `)}

    &:focus {
      border-color: ${({ $textColor }) => rgbAlpha($textColor, 1)};

      + span {
        ${hasContentState()}
      }
    }

    &[readonly] {
      opacity: .3;

      + span {
        opacity: .3;
      }
    }
  }

  > span {
    ${({ $placeholderVisible }) => $placeholderVisible ? hasContentState() : presets.font18InputHasContentLabel}
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    bottom: ${vw(12, 'mobile')};
    color: var(--color-black);
    opacity: 1;
    position: absolute;
    transform: translateY(-${vw(35, 'mobile')});
    transition: 300ms font-size ease, 300ms transform ease, 300ms opacity ease;
    white-space: nowrap;

    ${mq.greaterThan('nexus7')} {
      bottom: ${vw(12, 'tablet')};
      transform: translateY(-${vw(35, 'tablet')});
    }

    ${mq.greaterThan('tablet')} {
      bottom: ${vw(12, 'desktop')};
      transform: translateY(-${vw(35, 'desktop')});
    }

    ${mq.greaterThan('desktop')} {
      bottom: 12px;
      transform: translateY(-35px);
    }
  }

  figure, img {
    &.chevron {
      bottom: ${vw(21, 'mobile')};
      height: ${vw(8, 'mobile')};
      pointer-events: none;
      position: absolute;
      right: ${vw(20, 'mobile')};

      ${mq.lessThan('tablet')} {
        width: ${vw(12, 'mobile')};
      }

      ${mq.greaterThan('tablet')} {
        bottom: ${vw(21, 'desktop')};
        height: ${vw(8, 'desktop')};
        right: ${vw(20, 'desktop')};
        width: ${vw(12, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        bottom: 21px;
        height: 8px;
        right: 20px;
        width: 12px;
      }

      svg {
        height: 100%;
        object-fit: contain;
        transition: 300ms transform ease-out;
        width: 100%;
      }
    }
  }
`
